import { useState, useCallback } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook: owns the barcode-detail modal state + inline edit/delete actions.
 *
 * Caller provides `{ fetchJSON, onAfterMutation }`:
 *   - fetchJSON(url) — same auth-aware fetch from useAdminData
 *   - onAfterMutation() — typically `loadAll` so list counts refresh after an edit
 *
 * Returns the modal-state bundle + `openDetail`, `saveEdit`, `deleteEntry`, `close`.
 */
export default function useBarcodeDetailEditor({ fetchJSON, onAfterMutation }) {
  const [selectedBarcode, setSelectedBarcode] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const openDetail = useCallback(
    async (barcode) => {
      setSelectedBarcode(barcode);
      setDetail(null);
      setEditing(null);
      setEditValue('');
      const d = await fetchJSON(`${API}/api/admin/library/${encodeURIComponent(barcode)}`);
      setDetail(d);
    },
    [fetchJSON]
  );

  const saveEdit = useCallback(
    async (barcode, company) => {
      const price = parseFloat(editValue);
      if (isNaN(price) || price < 0) {
        alert('Enter a valid price (0 or greater).');
        return;
      }
      setSavingEdit(true);
      try {
        const res = await fetch(`${API}/api/admin/library/${encodeURIComponent(barcode)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ company, price }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await fetchJSON(`${API}/api/admin/library/${encodeURIComponent(barcode)}`);
        setDetail(d);
        await onAfterMutation?.();
        setEditing(null);
        setEditValue('');
      } catch (err) {
        alert(`Edit failed: ${err.message}`);
      } finally {
        setSavingEdit(false);
      }
    },
    [editValue, fetchJSON, onAfterMutation]
  );

  const deleteEntry = useCallback(
    async (barcode, company) => {
      if (
        !window.confirm(
          `Remove ${company} entry from barcode ${barcode}? This appends an audit row (deletion is logged, not silent).`
        )
      )
        return;
      setSavingEdit(true);
      try {
        const res = await fetch(
          `${API}/api/admin/library/${encodeURIComponent(barcode)}/${encodeURIComponent(company)}`,
          { method: 'DELETE', credentials: 'include' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const d = await fetchJSON(`${API}/api/admin/library/${encodeURIComponent(barcode)}`);
        setDetail(d);
        await onAfterMutation?.();
      } catch (err) {
        alert(`Delete failed: ${err.message}`);
      } finally {
        setSavingEdit(false);
      }
    },
    [fetchJSON, onAfterMutation]
  );

  const close = useCallback(() => setSelectedBarcode(null), []);

  return {
    selectedBarcode,
    detail,
    editing,
    editValue,
    savingEdit,
    setEditing,
    setEditValue,
    openDetail,
    saveEdit,
    deleteEntry,
    close,
  };
}
